import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import { uploadImage, deleteImage, getImageUrl } from '../../services/listings';

const ImageUploader = ({ value = [], onChange, multiple = true, maxFiles }) => {
  const [uploading, setUploading] = useState(false);
  const [images, setImages] = useState(value);

  const onDrop = useCallback(
    async (acceptedFiles) => {
      setUploading(true);
      try {
        // If maxFiles is set, limit the number of files
        let filesToUpload = acceptedFiles;
        if (maxFiles && !multiple) {
          filesToUpload = acceptedFiles.slice(0, 1);
        } else if (maxFiles && multiple) {
          const remainingSlots = maxFiles - images.length;
          filesToUpload = acceptedFiles.slice(0, Math.max(0, remainingSlots));
        }
        
        if (filesToUpload.length === 0) {
          alert(`Maximum ${maxFiles} image${maxFiles > 1 ? 's' : ''} allowed.`);
          setUploading(false);
          return;
        }

        const uploadPromises = filesToUpload.map((file) => uploadImage(file));
        const results = await Promise.all(uploadPromises);
        const newImageIds = results.map((result) => result.fileId);
        const updatedImages = multiple ? [...images, ...newImageIds] : newImageIds;
        
        // Enforce maxFiles limit
        const finalImages = maxFiles ? updatedImages.slice(0, maxFiles) : updatedImages;
        
        setImages(finalImages);
        onChange(finalImages);
      } catch (error) {
        console.error('Error uploading images:', error);
        alert('Error uploading images. Please try again.');
      } finally {
        setUploading(false);
      }
    },
    [images, multiple, onChange, maxFiles]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.webp'],
    },
    multiple: multiple && (!maxFiles || images.length < maxFiles),
    disabled: uploading || (maxFiles && images.length >= maxFiles),
  });

  const handleRemoveImage = async (imageId, index) => {
    try {
      await deleteImage(imageId);
      const updatedImages = images.filter((_, i) => i !== index);
      setImages(updatedImages);
      onChange(updatedImages);
    } catch (error) {
      console.error('Error deleting image:', error);
      alert('Error deleting image. Please try again.');
    }
  };

  return (
    <Box>
      <Box
        {...getRootProps()}
        sx={{
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'grey.300',
          borderRadius: 2,
          p: 3,
          textAlign: 'center',
          cursor: 'pointer',
          bgcolor: isDragActive ? 'action.hover' : 'background.paper',
          mb: 2,
        }}
      >
        <input {...getInputProps()} />
        <Typography variant="body1" sx={{ mb: 1 }}>
          {isDragActive
            ? 'Drop image here...'
            : maxFiles === 1
            ? 'Click to upload (1 image only)'
            : `Click to upload${maxFiles ? ` (max ${maxFiles})` : ''}`}
        </Typography>
        {uploading && <Typography variant="body2" color="primary">Uploading...</Typography>}
        {maxFiles && images.length >= maxFiles && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            Maximum {maxFiles} image{maxFiles > 1 ? 's' : ''} reached
          </Typography>
        )}
      </Box>

      {images.length > 0 && (
        <Grid container spacing={2}>
          {images.map((imageId, index) => (
            <Grid item xs={6} sm={4} md={3} key={index}>
              <Paper
                sx={{
                  position: 'relative',
                  paddingTop: '100%',
                  overflow: 'hidden',
                }}
              >
                <Box
                  component="img"
                  src={getImageUrl(imageId)}
                  alt={`Upload ${index + 1}`}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
                <IconButton
                  onClick={() => handleRemoveImage(imageId, index)}
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    bgcolor: 'error.main',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'error.dark',
                    },
                  }}
                  size="small"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default ImageUploader;
